import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { SKIP_ERROR_TOAST } from '../../../common/interceptors/skip-error-toast.token';
import { API_BASE_URL } from '../../../common/tokens/api-base-url.token';
import {
  ClientOrganizationAdmin,
  ClientOrganizationInput,
} from '../../application/entities/client-organization-admin.entity';
import { LocationConfig } from '../../application/entities/location-config.entity';
import { LocationConfigChangeSet } from '../../application/entities/location-config-change-set.entity';
import { ConfigurationProviderPort } from '../../application/ports/configuration-provider.port';
import {
  ClientOrganizationListResponseDto,
  ClientOrganizationResponseDto,
} from '../dto/client-organization.dto';
import { LocationConfigResponseDto } from '../dto/location-config-response.dto';
import {
  toClientOrganizationAdminFromResponse,
  toClientOrganizationAdminList,
  toClientOrganizationRequestDto,
} from '../mappers/client-organization.mapper';
import { toBatchRequestDto, toLocationConfig } from '../mappers/location-config.mapper';

/**
 * Las escrituras de organizaciones omiten el toast automático del interceptor
 * para que la pantalla muestre mensajes propios (409: listado duplicado o datos asociados).
 */
const OWN_ERROR_HANDLING = () => new HttpContext().set(SKIP_ERROR_TOAST, true);

@Injectable({ providedIn: 'root' })
export class ConfigurationProviderService implements ConfigurationProviderPort {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  getLocationConfig(organizationUuid: string): Observable<LocationConfig> {
    return this.http
      .get<LocationConfigResponseDto>(`${this.baseUrl}locations/${organizationUuid}/config`)
      .pipe(map(toLocationConfig));
  }

  saveLocationConfig(
    organizationUuid: string,
    changes: LocationConfigChangeSet,
  ): Observable<LocationConfig> {
    return this.http
      .post<LocationConfigResponseDto>(
        `${this.baseUrl}locations/${organizationUuid}/config`,
        toBatchRequestDto(changes),
      )
      .pipe(map(toLocationConfig));
  }

  listClientOrganizations(): Observable<ClientOrganizationAdmin[]> {
    return this.http
      .get<ClientOrganizationListResponseDto>(`${this.baseUrl}organizations/clients`)
      .pipe(map(toClientOrganizationAdminList));
  }

  createClientOrganization(input: ClientOrganizationInput): Observable<ClientOrganizationAdmin> {
    return this.http
      .post<ClientOrganizationResponseDto>(
        `${this.baseUrl}organizations`,
        toClientOrganizationRequestDto(input),
        { context: OWN_ERROR_HANDLING() },
      )
      .pipe(map(toClientOrganizationAdminFromResponse));
  }

  updateClientOrganization(
    organizationUuid: string,
    input: ClientOrganizationInput,
  ): Observable<ClientOrganizationAdmin> {
    return this.http
      .put<ClientOrganizationResponseDto>(
        `${this.baseUrl}organizations/${organizationUuid}`,
        toClientOrganizationRequestDto(input),
        { context: OWN_ERROR_HANDLING() },
      )
      .pipe(map(toClientOrganizationAdminFromResponse));
  }

  uploadClientOrganizationLogo(
    organizationUuid: string,
    file: File,
  ): Observable<ClientOrganizationAdmin> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.http
      .post<ClientOrganizationResponseDto>(
        `${this.baseUrl}organizations/${organizationUuid}/logo`,
        formData,
        { context: OWN_ERROR_HANDLING() },
      )
      .pipe(map(toClientOrganizationAdminFromResponse));
  }

  deleteClientOrganizationLogo(organizationUuid: string): Observable<ClientOrganizationAdmin> {
    return this.http
      .delete<ClientOrganizationResponseDto>(
        `${this.baseUrl}organizations/${organizationUuid}/logo`,
        { context: OWN_ERROR_HANDLING() },
      )
      .pipe(map(toClientOrganizationAdminFromResponse));
  }

  deleteClientOrganization(organizationUuid: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}organizations/${organizationUuid}`, {
        context: OWN_ERROR_HANDLING(),
      })
      .pipe(map(() => void 0));
  }
}
